import { useState, useMemo } from 'react';
import { useAppSelector } from 'store/hook';
import AssetCard from './components/AssetCard';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { ArrowBack, Search } from '@mui/icons-material';
import { type HealthScore } from 'types/squares.type';
import { type ServiceView } from 'types/survivalAnalysis.types';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const AllAssetHealthScoreView = ({
  handleShowAssetDetails,
}: {
  handleShowAssetDetails: (
    value: boolean | null,
    paramSelectedAsset?: ServiceView,
    selectedView?: string
  ) => void;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const healthData = useAppSelector(
    (state) => state.squaresDashboardReducer.healthScoreData
  );

  const [searchTerm, setSearchTerm] = useState('');

  if (!healthData || Object.keys(healthData).length === 0) {
    return <Typography>{t('no_assets_available')}</Typography>;
  }

  const validAssets = Object.entries(healthData).filter(
    ([, data]: [string, HealthScore]) =>
      data?.asset_details?.health_score !== null &&
      data?.asset_details?.health_score !== undefined
  );

  if (validAssets.length === 0) {
    return (
      <Typography>
        {t('no_valid_assets_with_health_score_available')}
      </Typography>
    );
  }

  // Filter & sort assets
  const filteredAndSortedAssets = useMemo(() => {
    // filter by search term
    const filteredAssets = validAssets.filter(([_, data]) => {
      const {
        bumper_number: bumperNumber,
        make,
        model,
        asset_type: assetType,
      } = data.asset_details;
      const lowerSearchTerm = searchTerm.toLowerCase();

      return (
        bumperNumber.toLowerCase().includes(lowerSearchTerm) ||
        make.toLowerCase().includes(lowerSearchTerm) ||
        model.toLowerCase().includes(lowerSearchTerm) ||
        assetType.toLowerCase().includes(lowerSearchTerm)
      );
    });

    // sort by health score
    const sortedAssets = filteredAssets.sort(([_, aData], [__, bData]) => {
      const aScore = aData.asset_details.health_score ?? 0;
      const bScore = bData.asset_details.health_score ?? 0;
      return aScore - bScore;
    });

    return sortedAssets;
  }, [validAssets, searchTerm]);

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2} py={2}>
      <Box display="flex" gap={2} alignItems="center">
        <Box>
          <CustomLoadingButton
            onClick={() => {
              handleShowAssetDetails(false, undefined, 'dashboard');
            }}
            startIcon={<ArrowBack />}
            variant="contained"
            color="secondary"
          >
            {t('back_to_dashboard')}
          </CustomLoadingButton>
        </Box>
        <TextField
          placeholder={t('search_for_an_asset_make_model_asset_type')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          InputProps={{
            sx: {
              height: '35px',
              minWidth: '400px',
              background: palette.boxBackground,
              color: palette.textPrimary,
              border: `0.5px solid ${palette.textSecondary}`,
              fontSize: '12px',
            },
            endAdornment: (
              <InputAdornment position="end">
                <Search
                  sx={{
                    color: palette.textPrimary,
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gridAutoRows: '1fr',
          gap: 3,
        }}
      >
        {filteredAndSortedAssets.map(([bumperNumber, data]) => (
          <AssetCard
            key={bumperNumber}
            asset={data}
            handleShowAssetDetails={handleShowAssetDetails}
          />
        ))}
      </Box>

      {filteredAndSortedAssets.length === 0 && (
        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          height={'400px'}
        >
          <Typography
            sx={{
              color: palette.textPrimary,
              fontSize: '18px',
              fontWeight: 600,
            }}
          >
            {t('no_assets_match_your_search')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
